import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../../../context/LanguageContext";
import { useAlert } from "../../../Hooks/useAlert";
import { customerAPI, customerLeaveAPI } from "../../../services/api";
import settingsLanguage from "../../../language/settingsLanguage";
import Alert from "../../common/Alert";
import ConfirmDialog from "../../common/ConfirmDialog";
import BackButton from "./BackButton";

function CustomerLeavePage() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const text = settingsLanguage[language];
  const { showAlert, showConfirm, alertConfig, confirmConfig } = useAlert();
  const [loading, setLoading] = useState(false);
  const [leaves, setLeaves] = useState([]);
  const [customers, setCustomers] = useState([]);

  // Form state
  const [editingLeaveId, setEditingLeaveId] = useState(null);
  const [customerSearch, setCustomerSearch] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadCustomers();
    loadLeaves();
  }, []);

  const filteredCustomers = useMemo(() => {
    if (!customerSearch) return customers;
    return customers.filter(
      (c) =>
        c.customerName.toLowerCase().includes(customerSearch.toLowerCase()) ||
        (c.whatsAppNo && c.whatsAppNo.includes(customerSearch))
    );
  }, [customerSearch, customers]);

  const loadCustomers = async () => {
    try {
      const response = await customerAPI.getCustomers();
      if (response.success) {
        setCustomers(response.data);
      }
    } catch (error) {
      console.error("Error loading customers:", error);
    }
  };

  const loadLeaves = async () => {
    try {
      setLoading(true);
      const response = await customerLeaveAPI.getLeaves();
      if (response.success) {
        setLeaves(response.data);
      }
    } catch (error) {
      console.error("Error loading leaves:", error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEditingLeaveId(null);
    setSelectedCustomer(null);
    setCustomerSearch("");
    setStartDate("");
    setEndDate("");
    setReason("");
  };

  const handleSelectCustomer = (customer) => {
    setSelectedCustomer(customer);
    setCustomerSearch(customer.customerName);
    setShowCustomerDropdown(false);
  };

  const handleEditLeave = (leave) => {
    setEditingLeaveId(leave.leaveId);
    const customer = customers.find((c) => c.customerId === leave.customerId);
    if (customer) {
      setSelectedCustomer(customer);
      setCustomerSearch(customer.customerName);
    }
    setStartDate(leave.startDate.split("T")[0]);
    setEndDate(leave.endDate.split("T")[0]);
    setReason(leave.reason || "");
  };

  const handleCancelEdit = () => {
    resetForm();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedCustomer) {
      await showAlert({ type: "error", title: "Error", message: "Please select a customer" });
      return;
    }
    if (!startDate || !endDate) {
      await showAlert({ type: "error", title: "Error", message: "Please select start and end dates" });
      return;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const sDate = new Date(startDate);
    const eDate = new Date(endDate);

    if (!editingLeaveId && sDate < today) {
      await showAlert({ type: "error", title: "Error", message: "Start date cannot be before today" });
      return;
    }
    if (eDate < sDate) {
      await showAlert({ type: "error", title: "Error", message: "End date cannot be before start date" });
      return;
    }

    try {
      setSaving(true);
      let response;
      if (editingLeaveId) {
        response = await customerLeaveAPI.updateLeave(
          editingLeaveId,
          selectedCustomer.customerId,
          startDate,
          endDate,
          reason
        );
      } else {
        response = await customerLeaveAPI.addLeave(
          selectedCustomer.customerId,
          startDate,
          endDate,
          reason
        );
      }
      if (response.success) {
        await showAlert({
          type: "success",
          title: "Success",
          message: editingLeaveId ? "Leave updated successfully" : "Leave added successfully",
        });
        resetForm();
        await loadLeaves();
      } else {
        await showAlert({ type: "error", title: "Error", message: response.message || "Operation failed" });
      }
    } catch (error) {
      console.error("Error saving leave:", error);
      if (error.response?.status === 404) {
        await showAlert({ type: "error", title: "Error", message: "Update endpoint not found. Please restart the API server." });
      } else {
        await showAlert({ type: "error", title: "Error", message: "Error saving leave" });
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteLeave = async (leave) => {
    const confirmed = await showConfirm({
      type: "warning",
      title: "Delete Leave",
      message: `Are you sure you want to delete the leave for ${leave.customerName} (${new Date(leave.startDate).toLocaleDateString()} - ${new Date(leave.endDate).toLocaleDateString()})?`,
      confirmText: "Delete",
      cancelText: "Cancel",
    });

    if (!confirmed) return;

    try {
      setLoading(true);
      const response = await customerLeaveAPI.deleteLeave(leave.leaveId);
      if (response.success) {
        await showAlert({ type: "success", title: "Success", message: "Leave deleted successfully" });
        if (editingLeaveId === leave.leaveId) resetForm();
        await loadLeaves();
      } else {
        await showAlert({ type: "error", title: "Error", message: response.message || "Failed to delete leave" });
      }
    } catch (error) {
      console.error("Error deleting leave:", error);
      await showAlert({ type: "error", title: "Error", message: "Error deleting leave" });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  };

  const isEditing = editingLeaveId !== null;

  return (
    <div className="settings-page">
      <header className="settings-page-header">
        <BackButton to="/settings" section="customer" />
        <h1 className="settings-page-title">{text.customerLeave}</h1>
      </header>

      <main className="settings-page-content">
        {/* Add/Edit Leave Form */}
        <div className="settings-form-card">
          <div className="settings-form-body">
            {isEditing && (
              <div style={{ marginBottom: "1rem", padding: "0.5rem 0.75rem", background: "#e0f2fe", borderRadius: "8px", fontSize: "0.8rem", color: "#0369a1" }}>
                <i className="fas fa-edit" style={{ marginRight: "0.375rem" }} />
                {text.editLeaveInstruction}
              </div>
            )}
            <div className="settings-form-group">
              <label className="settings-form-label">{text.customerName} *</label>
              <div style={{ position: "relative" }}>
                <input
                  type="text"
                  value={customerSearch}
                  onChange={(e) => {
                    setCustomerSearch(e.target.value);
                    setSelectedCustomer(null);
                    setShowCustomerDropdown(true);
                  }}
                  onFocus={() => setShowCustomerDropdown(true)}
                  onBlur={() => setTimeout(() => setShowCustomerDropdown(false), 200)}
                  className="settings-form-input"
                  placeholder={text.searchNamePhonePlaceholder}
                />
                {showCustomerDropdown && (
                  <div className="customer-leave-dropdown">
                    {filteredCustomers.length > 0 ? (
                      filteredCustomers.map((c) => (
                        <div
                          key={c.customerId}
                          className="customer-leave-option"
                          onMouseDown={() => handleSelectCustomer(c)}
                        >
                          {c.customerName}
                          {c.whatsAppNo && (
                            <span className="customer-leave-option-phone">
                              ({c.whatsAppNo})
                            </span>
                          )}
                        </div>
                      ))
                    ) : (
                      <div className="customer-leave-no-results">
                        {text.noCustomersMatchSearch}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="settings-form-group" style={{ marginBottom: 0 }}>
                <label className="settings-form-label">{text.startDate} *</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="settings-form-input"
                  min={editingLeaveId ? undefined : new Date().toISOString().split("T")[0]}
                  required
                />
              </div>
              <div className="settings-form-group" style={{ marginBottom: 0 }}>
                <label className="settings-form-label">{text.endDate} *</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="settings-form-input"
                  min={startDate || new Date().toISOString().split("T")[0]}
                  required
                />
              </div>
            </div>

            <div className="settings-form-group" style={{ marginTop: "1.25rem" }}>
              <label className="settings-form-label">{text.reasonOptional}</label>
              <input
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="settings-form-input"
                placeholder={text.reasonPlaceholder}
              />
            </div>

            <div className="settings-form-actions">
              <button
                type="button"
                onClick={handleSubmit}
                disabled={saving}
                className="settings-form-btn"
              >
                <i className={`fas ${isEditing ? "fa-save" : "fa-calendar-plus"}`} />
                <span>{saving ? text.saving : isEditing ? text.updateLeave : text.addLeave}</span>
              </button>
              {isEditing && (
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  className="settings-form-btn settings-form-btn-secondary"
                >
                  <i className="fas fa-times" />
                  <span>{text.cancel}</span>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Leaves Table */}
        <div className="settings-table-card">
          <h3 className="settings-table-title">{text.activeLeaves}</h3>
          {loading && leaves.length === 0 ? (
            <div className="settings-empty-state">
              <i className="fas fa-spinner fa-spin" />
              <p>{text.saving}</p>
            </div>
          ) : leaves.length > 0 ? (
            <div className="settings-table-wrapper">
              <table className="settings-table">
                <thead>
                  <tr>
                    <th className="settings-table-th settings-table-th-center">{text.actions}</th>
                    <th className="settings-table-th">{text.customerColumn}</th>
                    <th className="settings-table-th">{text.startDateColumn}</th>
                    <th className="settings-table-th">{text.endDateColumn}</th>
                    <th className="settings-table-th">{text.reasonColumn}</th>
                  </tr>
                </thead>
                <tbody>
                  {leaves.map((leave, index) => (
                    <tr key={leave.leaveId} className={`settings-table-tr ${index % 2 === 1 ? "settings-table-tr-zebra" : ""}`}>
                      <td className="settings-table-td settings-table-td-center">
                        <div className="settings-table-actions">
                          <button
                            type="button"
                            onClick={() => handleEditLeave(leave)}
                            className="settings-action-btn settings-action-btn-edit"
                            title={text.edit}
                          >
                            <i className="fas fa-pen" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteLeave(leave)}
                            className="settings-action-btn settings-action-btn-delete"
                            title={text.delete}
                          >
                            <i className="fas fa-trash-alt" />
                          </button>
                        </div>
                      </td>
                      <td className="settings-table-td settings-table-name">{leave.customerName}</td>
                      <td className="settings-table-td">{formatDate(leave.startDate)}</td>
                      <td className="settings-table-td">{formatDate(leave.endDate)}</td>
                      <td className="settings-table-td">{leave.reason || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="settings-empty-state">
              <i className="fas fa-calendar-times" />
              <p>{text.noLeavesYet}</p>
            </div>
          )}
        </div>
      </main>

      {alertConfig && <Alert {...alertConfig} />}
      {confirmConfig && <ConfirmDialog {...confirmConfig} />}
    </div>
  );
}

export default CustomerLeavePage;
